import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { UserPlus, Trash2, UserX, UserCheck, KeyRound, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserManagementTab = ({ 
  users, 
  currentUser, 
  passwordResetRequests, 
  getAuthHeaders, 
  onRefresh 
}) => {
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'dispatcher' });
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/role?role=${newRole}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('User role updated');
        onRefresh();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update user role');
      }
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newUser)
      });
      
      if (response.ok) {
        toast.success('User created successfully');
        setShowAddUser(false);
        setNewUser({ name: '', email: '', password: '', role: 'dispatcher' });
        onRefresh();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Failed to create user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('User deleted successfully');
        onRefresh();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleToggleUserActive = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/toggle-active`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        onRefresh();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update user status');
      }
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleApprovePasswordReset = async (requestId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/auth/password-reset-requests/${requestId}/approve`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ new_password: newPassword })
      });
      
      if (response.ok) {
        toast.success('Password reset approved');
        setShowResetPassword(null);
        setNewPassword('');
        onRefresh();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to approve password reset');
      }
    } catch (error) {
      toast.error('Failed to approve password reset');
    }
  };

  const handleRejectPasswordReset = async (requestId) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/password-reset-requests/${requestId}/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Password reset rejected');
        onRefresh();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reject password reset');
      }
    } catch (error) {
      toast.error('Failed to reject password reset');
    }
  };

  const isSuperAdmin = currentUser?.role === 'super_admin';

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>User Management</CardTitle>
          {isSuperAdmin && (
            <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
              <DialogTrigger asChild>
                <Button data-testid="add-user-btn">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New User</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Name *</Label>
                    <Input
                      data-testid="new-user-name"
                      placeholder="Full name"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      data-testid="new-user-email"
                      type="email"
                      placeholder="user@example.com"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Password *</Label>
                    <Input
                      data-testid="new-user-password"
                      type="password"
                      placeholder="Password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Select 
                      value={newUser.role} 
                      onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                    >
                      <SelectTrigger data-testid="new-user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="dispatcher">Dispatcher</SelectItem>
                        <SelectItem value="project_manager">Project Manager</SelectItem>
                        <SelectItem value="accounting">Accounting</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="create-user-btn" onClick={handleAddUser} className="w-full">
                    Create User
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {users.map((u) => (
              <div 
                key={u.user_id} 
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  u.is_active === false ? 'bg-gray-50 opacity-60' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  {u.picture ? (
                    <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">
                        {u.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{u.name}</p>
                      {u.is_active === false && (
                        <Badge variant="secondary" className="text-xs">Inactive</Badge>
                      )}
                      {u.role === 'super_admin' && (
                        <Badge variant="destructive" className="text-xs">Super Admin</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select 
                    value={u.role} 
                    onValueChange={(value) => handleUpdateUserRole(u.user_id, value)}
                    disabled={u.user_id === currentUser?.user_id || !isSuperAdmin}
                  >
                    <SelectTrigger className="w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="dispatcher">Dispatcher</SelectItem>
                      <SelectItem value="project_manager">Project Manager</SelectItem>
                      <SelectItem value="accounting">Accounting</SelectItem>
                    </SelectContent>
                  </Select>
                  {isSuperAdmin && u.user_id !== currentUser?.user_id && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleUserActive(u.user_id)}
                        title={u.is_active === false ? 'Activate User' : 'Deactivate User'}
                      >
                        {u.is_active === false ? (
                          <UserCheck className="h-4 w-4 text-green-600" />
                        ) : (
                          <UserX className="h-4 w-4 text-orange-600" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteUser(u.user_id, u.name)}
                        title="Delete User"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {users.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No users found</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Password Reset Requests (Super Admin only) */}
      {isSuperAdmin && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Password Reset Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            {passwordResetRequests.filter(r => r.status === 'pending').length > 0 ? (
              <div className="space-y-4">
                {passwordResetRequests.filter(r => r.status === 'pending').map((req) => (
                  <div key={req.request_id} className="flex items-center justify-between p-4 rounded-lg border bg-amber-50 border-amber-200">
                    <div>
                      <p className="font-medium">{req.user_name}</p>
                      <p className="text-sm text-muted-foreground">{req.email}</p>
                      <p className="text-xs text-amber-600 mt-1">
                        Requested: {new Date(req.requested_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {showResetPassword === req.request_id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="password"
                            placeholder="New password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-40"
                            data-testid={`reset-password-input-${req.request_id}`}
                          />
                          <Button
                            size="sm"
                            onClick={() => handleApprovePasswordReset(req.request_id)}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`confirm-reset-${req.request_id}`}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setShowResetPassword(null); setNewPassword(''); }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            onClick={() => setShowResetPassword(req.request_id)}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`approve-reset-${req.request_id}`}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRejectPasswordReset(req.request_id)}
                            data-testid={`reject-reset-${req.request_id}`}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">No pending password reset requests</p>
            )}
            
            {/* History of processed requests */}
            {passwordResetRequests.filter(r => r.status !== 'pending').length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Recent History</h4>
                <div className="space-y-2">
                  {passwordResetRequests.filter(r => r.status !== 'pending').slice(0, 5).map((req) => (
                    <div key={req.request_id} className="flex items-center justify-between p-3 rounded-lg border bg-gray-50">
                      <div>
                        <p className="text-sm font-medium">{req.user_name} ({req.email})</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(req.reviewed_at).toLocaleString()}
                        </p>
                      </div>
                      <Badge variant={req.status === 'approved' ? 'default' : 'destructive'}>
                        {req.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default UserManagementTab;
